import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Toast } from '@pletly/ui';

import { useResetMemberPasswordMutation } from './__generated__/resetMemberPassword.generated';

export const useResetMemberPassword = () => {
	const { t } = useTranslation();
	const [resetMemberPassword] = useResetMemberPasswordMutation();
	const [memberIdForModal, setMemberIdForModal] = useState<string | undefined>();

	const handleReset = useCallback(
		async (memberId: string, newPassword?: string) => {
			try {
				await resetMemberPassword({ variables: { id: memberId, newPassword } });
				Toast.show({
					type: 'success',
					text1: t('toasts.success.reset-password-mail-sent'),
				});
			} catch {
				Toast.show({
					type: 'error',
					text1: t('toasts.error.something-wrong'),
				});
			}
		},
		[resetMemberPassword, t],
	);

	const handleResetPress = useCallback(
		({ memberId, hasEmail }: { memberId: string; hasEmail: boolean }) => {
			if (hasEmail) {
				handleReset(memberId);
			} else {
				setMemberIdForModal(memberId);
			}
		},
		[handleReset],
	);

	const closeChangePasswordModal = useCallback(() => {
		setMemberIdForModal(undefined);
	}, []);

	const handleChangePassword = useCallback(
		async ({ newPassword }: { newPassword: string }) => {
			if (!memberIdForModal) {
				return;
			}

			return handleReset(memberIdForModal, newPassword);
		},
		[handleReset, memberIdForModal],
	);

	return {
		handleResetPress,
		changePasswordModal: {
			visible: !!memberIdForModal,
			close: closeChangePasswordModal,
		},
		handleChangePassword,
	};
};
